import { useEffect, useMemo, useState, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from '@/components/dialogBox';
import { ProductType, RegimeLogic } from '@/constant';
import countries from 'world-countries';
import {
  useGetProductVatConfigsQuery,
  useDeleteProductVatConfigMutation,
} from '@/services/ProductVatConfigService';
import {
  ProductVatConfigData,
  ProductVatConfigQueryParams,
} from '@/types/product-vat-config.types';
import {
  defaultProductVatConfigScreen,
  type ProductVatConfigScreenConfig,
} from './productVatConfigScreenConfig';
import {
  isProductVatConfigActive,
  normalizeProductVatConfigDate,
} from './productVatConfigUtils';
import { formatFinnishPercentage } from '@/utils';
import { formatDisplayDate } from '@/utils/Date';

interface ProductVatConfigListingsProps {
  screenConfig?: ProductVatConfigScreenConfig;
}

export const ProductVatConfigListings = ({
  screenConfig = defaultProductVatConfigScreen,
}: ProductVatConfigListingsProps) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const {
    pagination,
    setPagination,
    totalItems,
    totalPages,
    handleTotalItems,
    handleTotalPages,
  } = usePagination();

  const queryParams: ProductVatConfigQueryParams = useMemo(() => {
    return {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      ...(screenConfig.listQueryParams ?? {}),
    };
  }, [pagination.pageIndex, pagination.pageSize, screenConfig.listQueryParams]);

  const { data, isLoading } = useGetProductVatConfigsQuery(queryParams);

  useEffect(() => {
    if (data) {
      const count = data.totalItems ?? data.data?.length ?? 0;
      const pages =
        data.totalPages ??
        Math.max(1, Math.ceil(count / (pagination.pageSize || 10)));
      handleTotalItems(count);
      handleTotalPages(pages);
    }
  }, [data, handleTotalItems, handleTotalPages, pagination.pageSize]);

  const [deleteProductVatConfig] = useDeleteProductVatConfigMutation();
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    id: string | null;
  }>({ isOpen: false, id: null });

  const handleDeactivate = useCallback(
    async (id: string) => {
      try {
        await deleteProductVatConfig(id).unwrap();
        enqueueSnackbar('VAT config Deactivated successfully', {
          variant: 'success',
        });
        setConfirmState({ isOpen: false, id: null });
      } catch (e: unknown) {
        const apiMessage = (e as { data?: { message?: string } })?.data
          ?.message;
        enqueueSnackbar(apiMessage || 'Failed to Deactivate VAT config', {
          variant: 'error',
        });
      }
    },
    [deleteProductVatConfig, enqueueSnackbar]
  );

  const productTypeLabels: Record<ProductType, string> = useMemo(
    () => ({
      [ProductType.HARBOUR_BERTH]: 'Harbour berth',
      [ProductType.HARBOUR_SERVICE]: 'Harbour service',
      [ProductType.HARBOUR_ADDON]: 'Harbour addon',
      [ProductType.SAARISTO_SUBSCRIPTION]: 'Saaristo subscription',
      [ProductType.CLUB_RESOURCE]: 'Club resource',
      [ProductType.SERVICE_BOOKING]: 'Service booking',
      [ProductType.OTHER]: 'Other',
    }),
    []
  );

  const regimeLogicLabels: Record<RegimeLogic, string> = useMemo(
    () => ({
      [RegimeLogic.DOMESTIC_ONLY]: 'Domestic only',
      [RegimeLogic.BY_CUSTOMER_COUNTRY]: 'By customer country',
      [RegimeLogic.IOSS_ELIGIBLE]: 'IOSS eligible',
    }),
    []
  );

  const countryNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    countries.forEach((c) => {
      if (c.cca2) {
        const code = c.cca2;
        const name = c.name?.common || code;
        map[code] = name;
      }
    });
    return map;
  }, []);

  const columns = useMemo(
    () =>
      [
        ...(screenConfig.hideProductTypeColumn
          ? []
          : [
            {
              accessorKey: 'product_type',
              header: 'Product Type',
              enableSorting: false,
              cell: ({
                row,
              }: {
                row: { original: { data: ProductVatConfigData } };
              }) => (
                <span className="text-sm text-gray-900">
                  {row.original.data.product_type
                    ? (productTypeLabels[
                      row.original.data.product_type as ProductType
                    ] ?? row.original.data.product_type)
                    : '-'}
                </span>
              ),
            },
          ]),
        {
          accessorKey: 'default_vat_rate',
          header: 'VAT %',
          enableSorting: false,
          cell: ({
            row,
          }: {
            row: { original: { data: ProductVatConfigData } };
          }) => (
            <span className="text-sm text-gray-900">
              {formatFinnishPercentage(row.original.data.default_vat_rate)}
            </span>
          ),
        },
        {
          accessorKey: 'default_regime_logic',
          header: 'Regime Logic',
          enableSorting: false,
          cell: ({
            row,
          }: {
            row: { original: { data: ProductVatConfigData } };
          }) => (
            <span className="text-sm text-gray-900">
              {(() => {
                const cfg = row.original.data;
                const baseLabel =
                  regimeLogicLabels[cfg.default_regime_logic as RegimeLogic] ??
                  cfg.default_regime_logic;

                let countryCode: string | undefined;
                if (cfg.default_regime_logic === RegimeLogic.DOMESTIC_ONLY) {
                  countryCode = cfg.domestic_country_code || undefined;
                } else if (
                  cfg.default_regime_logic === RegimeLogic.BY_CUSTOMER_COUNTRY
                ) {
                  countryCode = cfg.customer_country_code || undefined;
                }

                const countryName = countryCode
                  ? countryNameMap[countryCode] || countryCode
                  : null;

                return countryName
                  ? `${baseLabel} (${countryName})`
                  : baseLabel;
              })()}
            </span>
          ),
        },
        ...(screenConfig.showIossEligibleColumn === false
          ? []
          : [
            {
              accessorKey: 'eligible_for_ioss',
              header: 'IOSS Eligible',
              enableSorting: false,
              cell: ({
                row,
              }: {
                row: { original: { data: ProductVatConfigData } };
              }) => (
                <span className="text-sm text-gray-900">
                  {row.original.data.eligible_for_ioss ? 'Yes' : 'No'}
                </span>
              ),
            },
          ]),
        {
          accessorKey: 'active_from',
          header: 'Active From',
          enableSorting: false,
          cell: ({
            row,
          }: {
            row: { original: { data: ProductVatConfigData } };
          }) => (
            <span className="text-sm text-gray-900">
              {formatDisplayDate(row.original.data.active_from, '-')}
            </span>
          ),
        },
        {
          accessorKey: 'active_to',
          header: 'Active To',
          enableSorting: false,
          cell: ({
            row,
          }: {
            row: { original: { data: ProductVatConfigData } };
          }) => (
            <span className="text-sm text-gray-900">
              {formatDisplayDate(row.original.data.active_to, '-')}
            </span>
          ),
        },
        {
          accessorKey: 'status',
          header: 'Status',
          enableSorting: false,
          cell: ({
            row,
          }: {
            row: { original: { data: ProductVatConfigData } };
          }) => {
            const cfg = row.original.data;
            const isInactive = !isProductVatConfigActive(cfg);

            return (
              <span
                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${isInactive
                  ? 'bg-danger text-white'
                  : 'bg-success text-[#052e16] dark:text-white contrast:text-white'
                  }`}
              >
                {isInactive ? 'Inactive' : 'Active'}
              </span>
            );
          },
        },
        {
          accessorKey: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({
            row,
          }: {
            row: { original: { data: ProductVatConfigData } };
          }) => {
            const cfg = row.original.data;
            const todayStr = normalizeProductVatConfigDate(
              new Date().toISOString()
            );
            const configActiveTo = normalizeProductVatConfigDate(cfg.active_to);
            const showDeactivate =
              !cfg.active_to || configActiveTo !== todayStr;
            return (
              <div className="flex items-center gap-2">
                {hasPermission('product_vat_config_management_read') && (
                  <button
                    onClick={() =>
                      navigate(
                        `${screenConfig.basePath}/edit/${cfg.id}?mode=view`
                      )
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline"
                    title="View"
                  >
                    <KeenIcon icon="eye" />
                  </button>
                )}
                {hasPermission('product_vat_config_management_update') && (
                  <button
                    onClick={() =>
                      navigate(`${screenConfig.basePath}/edit/${cfg.id}`)
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline"
                    title="Edit"
                  >
                    <KeenIcon icon="notepad-edit" />
                  </button>
                )}
                {hasPermission('product_vat_config_management_update') &&
                  showDeactivate && (
                    <button
                      onClick={() =>
                        setConfirmState({ isOpen: true, id: cfg.id })
                      }
                      className="btn btn-sm btn-icon btn-light btn-outline btn-danger"
                      title="Deactivate"
                    >
                      <KeenIcon icon="shield-slash" />
                    </button>
                  )}
              </div>
            );
          },
        },
      ].filter(Boolean),
    [
      hasPermission,
      navigate,
      productTypeLabels,
      regimeLogicLabels,
      countryNameMap,
      screenConfig.basePath,
      screenConfig.hideProductTypeColumn,
      screenConfig.showIossEligibleColumn,
    ]
  );

  const gridData = useMemo(
    () => (data?.data ?? []).map((d) => ({ data: d })),
    [data]
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <DataGrid
          columns={columns}
          data={gridData}
          messages={{
            loading: 'Loading VAT configs...',
            empty: 'No VAT configs found',
          }}
          pagination={{
            page: pagination.pageIndex,
            size: pagination.pageSize,
            more: true,
            totalPages: totalPages,
            totalItems: totalItems,
            setPagination,
            info: `{from} - {to} of {count}`,
            sizes: [5, 10, 25, 50, 100],
            sizesLabel: 'Show',
            sizesDescription: 'per page',
          }}
          serverSide={true}
          loading={isLoading}
        />
      </div>

      <ConfirmationDialog
        open={confirmState.isOpen}
        onClose={() => setConfirmState({ isOpen: false, id: null })}
        onConfirm={() => confirmState.id && handleDeactivate(confirmState.id)}
        message="Are you sure you want to deactivate this configuration?"
        confirmButtonText="Deactivate"
        cancelButtonText="Cancel"
        confirmButtonVariant="destructive"
      />
    </div>
  );
};
